/**
 * PublishShiftDrawer (BON-240) — offer an open position on an upcoming
 * workshop to the region. Pick a workshop (next 6 weeks), pick a position,
 * publish. Pay per position and the eligible-contractor count from the
 * design are BON-245 / BON-242 — em-dashed here, not invented.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { publishOpenShift } from '../../../api/openShifts';
import type { CrewPosition } from '../../../api/workshops';
import { viewerDate } from '../../../utils/viewer-date';

import { useRegionalWorkshops } from './regionalApi';
import { PositionSection } from './ShiftPositionSection';
import { WorkshopSection } from './ShiftWorkshopSection';

const DRAWER_DAYS = 42;

function drawerWindow(): { fromDate: string; toDate: string } {
  return { fromDate: viewerDate(0), toDate: viewerDate(DRAWER_DAYS - 1) };
}

const SCRIM_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  justifyContent: 'flex-end',
  zIndex: 60,
};

const ASIDE_STYLE: React.CSSProperties = {
  width: 380,
  maxWidth: '90vw',
  height: '100%',
  overflowY: 'auto',
  background: 'var(--nb-bg)',
  borderLeft: '1px solid var(--nb-border)',
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

export interface PublishShiftDrawerProps {
  regionSlug: string;
  onClose: () => void;
}

export function PublishShiftDrawer({ regionSlug, onClose }: PublishShiftDrawerProps) {
  const qc = useQueryClient();
  const [workshopId, setWorkshopId] = useState<string | null>(null);
  const [position, setPosition] = useState<CrewPosition>('teacher');

  const [{ fromDate, toDate }] = useState(drawerWindow);
  // Only ACTIVE workshops can take a shift (open-shifts.service rejects the
  // rest), so drafts/cancelled never reach the picker (BON-474).
  const workshops = useRegionalWorkshops(regionSlug, fromDate, toDate, 'active');

  const publish = useMutation({
    mutationFn: publishOpenShift,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards.regional', regionSlug, 'shifts'] });
      onClose();
    },
  });

  // While the publish is in flight the drawer must not silently unmount —
  // the user would lose the success/error signal. Escape, scrim, and Cancel
  // all funnel through this guard.
  const pending = publish.isPending;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pending]);

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !pending) onClose();
      }}
      style={SCRIM_STYLE}
    >
      <aside role="dialog" aria-modal="true" aria-label="Publish a shift" style={ASIDE_STYLE}>
        <header>
          <h3 style={{ margin: 0, fontSize: 15 }}>Publish a shift</h3>
          <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--nb-subtle)' }}>
            Offer an open position on a workshop to teachers in your region.
          </p>
        </header>

        <WorkshopSection workshops={workshops} workshopId={workshopId} onSelect={setWorkshopId} />

        <PositionSection position={position} onSelect={setPosition} />

        {publish.isError && (
          <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
            {(publish.error as Error).message}
          </div>
        )}

        <footer style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
          <button
            type="button"
            className="nb-btn nb-btn-ghost"
            onClick={onClose}
            disabled={publish.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="nb-btn nb-btn-primary"
            style={{ flex: 1 }}
            disabled={!workshopId || publish.isPending}
            onClick={() => {
              if (workshopId) publish.mutate({ workshopId, position });
            }}
          >
            {publish.isPending ? 'Publishing…' : 'Publish shift'}
          </button>
        </footer>
      </aside>
    </div>
  );
}
